import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Button,
  message,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Modal,
  Table,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  PlusCircleOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import API_BASE_URL from "../components/apiService";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

function OpenTrayecto() {
  const [loading, setLoading] = useState(false);
  const [trayectos, setTrayectos] = useState([]);
  const [selectedTrayecto, setSelectedTrayecto] = useState("");
  const [viajes, setViajes] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [vehiculosPorViaje, setVehiculosPorViaje] = useState({});
  const [totalJornada, setTotalJornada] = useState(0);

  const navigate = useNavigate();
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user && user.user_type_id === 1;

  // =========================
  // HELPERS
  // =========================
  // CHANGED: obtiene el id de trayecto desde cualquier nombre posible que venga del API
  const getTrayectoIdFromViaje = (v) =>
    v?.id_trayecto ??
    v?.trayecto_id ??
    v?.trayectos_id ??
    v?.id_trayectos ??
    null;

  // ✅ Parser robusto para precio (soporta number, "400000", "400.000", "400,000.00", "$ 40.000", etc.)
  const parsePrecio = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;

    let s = String(value).trim();

    // quita símbolos y espacios (deja dígitos, punto, coma, signo -)
    s = s.replace(/[^\d.,-]/g, "");

    // Si tiene '.' y ',' asumimos formato ES: '.' miles y ',' decimales
    if (s.includes(".") && s.includes(",")) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else if (s.includes(",")) {
      // Si solo tiene ',' asumimos ',' como decimales
      s = s.replace(",", ".");
    } else {
      // Si solo tiene '.', lo dejamos como decimal
      // (si tu backend manda "." como miles y no manda decimales, puedes cambiar esta regla)
    }

    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const formatNumber = (num) => {
    if (isNaN(num)) return num;
    const numberWithoutDecimals = Math.floor(num);
    return numberWithoutDecimals
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const navigateJornada = () => navigate("/jornadas");
  const recargarPagina = () => window.location.reload();

  // =========================
  // Obtener trayectos
  // =========================
  const fetchTrayectos = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/trayectos/all`, {
        method: "GET",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setTrayectos(data.data.trayectos);
      } else {
        message.error("No se pudieron cargar los trayectos");
      }
    } catch (error) {
      console.error("Error al obtener trayectos:", error);
      message.error("Error al obtener los trayectos");
    }
  };

  // =========================
  // Obtener viajes
  // =========================
  const fetchViajes = async (pageParam, limitParam, search = "") => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/operator/viajes/abiertos?page=${pageParam}&limit=${limitParam}&search=${encodeURIComponent(
          search
        )}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setViajes(data.data.viajes);
        setTotal(data.data.total);
      } else {
        message.error("No se pudieron cargar los viajes");
      }
    } catch (error) {
      console.error("Error al obtener viajes:", error);
    }
  };

  // =========================
  // Mostrar info vehículo (modal)
  // =========================
  const showVehicleInfo = async (vehiculo_id) => {
    const token = localStorage.getItem("token");
    setLoading(true);
    setModalVisible(true);
    setVehicleInfo(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/admin/vehiculo/${vehiculo_id}`, {
        headers: { Authorization: `${token}` },
      });

      setVehicleInfo({
        ...response.data.data.vehiculo,
        imagen: response.data.imagen || "https://via.placeholder.com/150",
      });
    } catch (error) {
      console.error("Error al obtener la información del vehículo:", error);
      setVehicleInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Cerrar trayecto
  // =========================
  const handleDeactivate = async (record) => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/operator/viajes/eliminar?id_viaje=${record.id_viaje}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.success && data.data.viajes[0].result === 1) {
        message.success("Trayecto cerrado con éxito y vehículo(s) eliminados");
        fetchViajes(page, limit, searchText);
      } else {
        message.error("No se pudo cerrar el trayecto");
      }
    } catch (error) {
      console.error("Error al cerrar trayecto:", error);
      message.error("Error al cerrar trayecto");
    }
  };

  // =========================
  // Activar/Desactivar trayecto (flag)
  // =========================
  const handleDeleteTrayecto = async (record) => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    try {
      const body = {
        id_viaje: record.id_viaje,
        is_delete: record.is_delete == 1 ? 0 : 1,
      };

      const response = await fetch(`${API_BASE_URL}/admin/trayecto/delete`, {
        method: "POST",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        message.success("Trayecto eliminado con éxito");
        fetchViajes(page, limit, searchText);
      } else {
        message.error("No se pudo eliminar el trayecto");
      }
    } catch (error) {
      console.error("Error al eliminar trayecto:", error);
      message.error("Error al eliminar trayecto");
    }
  };

  // =========================
  // Eliminar vehículo de un viaje
  // =========================
  const handleDelete = async (id_vehiculo, id_viaje) => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/operator/viajes/vehiculos/eliminar`, {
        method: "DELETE",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id_vehiculo, id_viaje }),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Vehículo eliminado con éxito");

        setVehiculosPorViaje((prev) => {
          const rows = prev[id_viaje] || [];
          return {
            ...prev,
            [id_viaje]: rows.filter((v) => v.id_vehiculo !== id_vehiculo),
          };
        });
      } else {
        message.error("No se pudo eliminar el vehículo");
      }
    } catch (error) {
      console.error("Error al eliminar vehículo:", error);
      message.error("Error al eliminar vehículo");
    }
  };

  // =========================
  // Obtener vehículos de un viaje específico
  // =========================
  const fetchVehiculos = async (id_viaje, trayecto_id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("No se encontró el token en el almacenamiento.");
      return;
    }

    // CHANGED: evita pegarle al API con trayecto_id undefined (eso provoca mismo resultado para todos)
    if (!trayecto_id) {
      console.warn("[fetchVehiculos] trayecto_id inválido para id_viaje:", id_viaje);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/operator/viajes/vehiculos?id_viaje=${id_viaje}&trayectos_id=${trayecto_id}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        const rows = Array.isArray(data.data.viajes) ? data.data.viajes : [];

        // CHANGED: clona y fuerza id_viaje en cada fila (por si el backend no lo manda)
        setVehiculosPorViaje((prev) => ({
          ...prev,
          [id_viaje]: rows.map((r) => ({ ...r, id_viaje })),
        }));
      } else {
        message.error("No se pudieron cargar los vehículos");
      }
    } catch (error) {
      console.error("Error al obtener vehículos:", error);
      message.error("Error al obtener los vehículos");
    }
  };

  // =========================
  // useEffects
  // =========================
  useEffect(() => {
    fetchTrayectos();

    // ✅ IMPORTANTE: limpiar cache viejo para que el total no sume viajes que ya no se muestran
    setVehiculosPorViaje({});
    setTotalJornada(0);

    fetchViajes(page, limit, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, searchText]);

  // CHANGED: Precarga vehículos cuando cambian los viajes (y con trayectoId correcto)
  useEffect(() => {
    (viajes || []).forEach((v) => {
      const trayectoId = getTrayectoIdFromViaje(v);

      if (!trayectoId) {
        console.warn("Viaje sin trayectoId (revisa nombre de propiedad):", v);
        return;
      }

      if (!vehiculosPorViaje[v.id_viaje]) {
        fetchVehiculos(v.id_viaje, trayectoId);
      }
    });

    // OJO: a propósito NO ponemos vehiculosPorViaje en deps para evitar loop infinito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viajes]);

  // ✅ Total jornada: suma SOLO los viajes visibles (los del state "viajes")
  useEffect(() => {
    const idsVisibles = (viajes || []).map((v) => v.id_viaje);

    const sum = idsVisibles.reduce((acc, id) => {
      const rows = Array.isArray(vehiculosPorViaje?.[id]) ? vehiculosPorViaje[id] : [];
      const subtotal = rows.reduce((a, r) => a + parsePrecio(r?.precio), 0);
      return acc + subtotal;
    }, 0);

    setTotalJornada(sum);
  }, [vehiculosPorViaje, viajes]);

  // =========================
  // Abrir trayecto
  // =========================
  const handleOpenTrayecto = async () => {
    if (!selectedTrayecto) {
      message.error("Por favor seleccione un trayecto");
      return;
    }

    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");
    const fecha_crea = `${year}-${month}-${day} ${new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}:59`;

    const token = localStorage.getItem("token");
    if (!token) {
      message.error("Token no encontrado.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/operator/open-trayecto`, {
        method: "POST",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          trayecto_id: selectedTrayecto,
          fecha_crea,
        }),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Trayecto abierto con éxito");
        navigate("/facturacion/" + data.data.response + "/" + selectedTrayecto);
      } else {
        message.error(data.error.message);
      }
    } catch (error) {
      console.error("Error al abrir el trayecto:", error);
      message.error("Error al abrir el trayecto");
    }
  };

  return (
    <div className="container" style={{ padding: "30px" }}>
      <Title level={2}>Trayecto</Title>

      {/* ✅ Total de la jornada (suma SOLO lo visible) */}
      <Card style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>
          Total de la jornada:{" "}
          <span className="stilosTotal">{formatNumber(totalJornada)}</span>
        </Title>
      </Card>

      <Form layout="vertical" onFinish={handleOpenTrayecto}>
        <Form.Item
          label="Seleccionar Trayecto"
          name="trayecto"
          rules={[{ required: true, message: "Por favor seleccione un trayecto" }]}
        >
          <Select
            value={selectedTrayecto}
            onChange={setSelectedTrayecto}
            placeholder="Seleccione un trayecto"
            style={{ height: 60 }}
          >
            {trayectos.map((trayecto) => (
              <Option
                key={trayecto.id_trayecto}
                value={trayecto.id_trayecto}
                style={{ fontSize: "20px" }}
              >
                {trayecto.trayecto}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row justify="center">
          <Col span={24}>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                width: 365,
                height: 60,
              }}
            >
              Abrir Nuevo Trayecto
            </Button>
            <br />
            <Button
              type="primary"
              onClick={navigateJornada}
              style={{
                marginBottom: 20,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                width: 365,
                height: 60,
              }}
            >
              Regresar a Jornadas
            </Button>
          </Col>
        </Row>
      </Form>

      <Row gutter={[16, 16]}>
        {viajes.map((viaje) => {
          const trayectoId = getTrayectoIdFromViaje(viaje);

          return (
            <Col span={24} key={viaje.id_viaje}>
              <Card title={`TR-${viaje.id_viaje}`} bordered={false} style={{ width: "100%" }}>
                <p>
                  <strong className="titles">Usuario:</strong> <br /> {viaje.nombre}
                </p>
                <p>
                  <strong className="titles">Trayecto:</strong> <br /> {viaje.trayecto}
                </p>
                <p>
                  <strong className="titles">Fecha:</strong> <br />{" "}
                  {new Date(viaje.fecha_creacion).toLocaleString()}
                </p>

                <p>
                  <strong className="titles">Estado:</strong> <br />
                  <Tag color={viaje.is_delete === 1 ? "green" : "red"}>
                    {viaje.is_delete === 1 ? "Trayecto Activo" : "Trayecto Inactivo"}
                  </Tag>
                  <Tag color={viaje.estado === 1 ? "green" : "red"}>
                    {viaje.estado === 1 ? "Trayecto abierto" : "Trayecto cerrado"}
                  </Tag>
                </p>

                <hr />
                <Title level={2}>Vehículos cargados</Title>

                <Table
                  rowKey="id_vehiculo"
                  columns={[
                    { title: "Placa", dataIndex: "matricula", key: "matricula", width: "12%" },
                    { title: "Tipo", dataIndex: "tipo_vehiculo", key: "tipo_vehiculo", width: "12%" },
                    {
                      title: "Tarifa",
                      dataIndex: "precio",
                      key: "precio",
                      render: (precio) => formatNumber(parsePrecio(precio)),
                      width: "12%",
                    },
                    {
                      title: "",
                      render: (text, record) => (
                        <div>
                          <Button
                            type="default"
                            icon={<PoweroffOutlined />}
                            onClick={() => handleDelete(record.id_vehiculo, viaje.id_viaje)}
                          />
                          <br />
                          <br />
                          {record.foto != null && (
                            <Button
                              icon={<FileImageOutlined />}
                              onClick={() => showVehicleInfo(record.id_vehiculo)}
                            />
                          )}
                        </div>
                      ),
                    },
                  ]}
                  dataSource={vehiculosPorViaje[viaje.id_viaje] || []}
                  pagination={false}
                  scroll={{ x: 200 }}
                  style={{ marginBottom: "20px" }}
                  footer={() => {
                    const rows = vehiculosPorViaje[viaje.id_viaje] || [];
                    const totalTrayecto = rows.reduce(
                      (acc, r) => acc + parsePrecio(r?.precio),
                      0
                    );
                    const totalFormatted = formatNumber(totalTrayecto);
                    return (
                      <tr>
                        <td colSpan={3}>
                          <strong className="titles">
                            Recaudo actual trayecto: {"\u00A0"}
                          </strong>
                        </td>
                        <td>
                          <strong className="stilosTotal">{totalFormatted}</strong>
                        </td>
                      </tr>
                    );
                  }}
                />

                <div>
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={() => navigate(`/facturacion/${viaje.id_viaje}/${trayectoId}`)}
                    style={{ marginBottom: "10px", width: 365, height: 60 }}
                  >
                    Registrar vehículo
                  </Button>

                  <Button
                    type="primary"
                    icon={<PoweroffOutlined />}
                    onClick={() => handleDeactivate(viaje)}
                    style={{ marginBottom: "10px", width: 365, height: 60 }}
                    disabled={viaje.estado === 0}
                  >
                    Cerrar trayecto
                  </Button>

                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => recargarPagina()}
                    style={{ marginBottom: "10px", width: 365, height: 60 }}
                  >
                    Actualizar
                  </Button>

                  {viaje.is_delete != undefined}
                  <Button
                    type="primary"
                    icon={<PoweroffOutlined />}
                    style={{ marginBottom: "10px", width: 365, height: 60 }}
                    onClick={() => handleDeleteTrayecto(viaje)}
                  >
                    {viaje.is_delete == 1 ? "Desactivar trayecto" : "Activar trayecto"}
                  </Button>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        title="Información del Vehículo"
        open={modalVisible} // ✅ AntD v5
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <img
          src={
            vehicleInfo && vehicleInfo.foto
              ? `${API_BASE_URL}/uploads/${vehicleInfo.foto}`
              : "assets/course-default.jpg"
          }
          alt="Imagen del vehículo"
          width={200}
        />
        <p>
          <strong>Matricula:</strong> {vehicleInfo ? vehicleInfo.matricula : "N/A"}
        </p>
        <p>
          <strong>Tipo de Vehículo:</strong> {vehicleInfo ? vehicleInfo.tipo_vehiculo : "N/A"}
        </p>
        <p>
          <strong>Conductor:</strong> {vehicleInfo ? vehicleInfo.nombre : "N/A"}
        </p>
        <p>
          <strong>Telefono:</strong> {vehicleInfo ? vehicleInfo.telefono : "N/A"}
        </p>
        <p>
          <strong>Fecha de registro:</strong>{" "}
          {vehicleInfo ? vehicleInfo.vehiculo_fecha_creacion : "N/A"}
        </p>
      </Modal>
    </div>
  );
}

export default OpenTrayecto;
